import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { MerchantClient } from '@/hooks/useMerchantClients';
import { Field, inputClass } from '@/pages/dashboard/finance/components/FinanceUI';

export interface CustomerFields {
  name: string;
  phone: string;
  email: string;
  address: string;
  ice: string;
}

interface Props {
  merchantId: number | null;
  clients: MerchantClient[];
  customer: CustomerFields;
  onChange: (patch: Partial<CustomerFields>) => void;
  onClientCreated: () => void;
}

// Bloc 2 — client : sélection dans la base ou création rapide.
export default function ClientPickerBlock({ merchantId, clients, customer, onChange, onClientCreated }: Props) {
  const [selectedId, setSelectedId] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '' });

  const pickClient = (id: string) => {
    setSelectedId(id);
    if (!id) return;
    const found = clients.find((c) => c.clientId === id);
    if (!found) return;
    onChange({
      name: found.name || (found.clientId.startsWith('guest:') ? found.clientId.slice(6) : ''),
      phone: found.phone || '',
      email: found.email || '',
      address: found.address || '',
    });
  };

  const createClient = async () => {
    if (!form.name.trim()) { setError('Le nom du client est obligatoire.'); return; }
    if (!merchantId) { setError('Compte introuvable.'); return; }
    setBusy(true);
    setError(null);
    try {
      const idclient = `inv-${Date.now()}`;
      const { error: insErr } = await supabase.from('clientshop').insert({
        idshop: merchantId,
        idclient,
        nomclient: form.name.trim(),
        telephone: form.phone.trim() || null,
        email: form.email.trim() || null,
        adresse: form.address.trim() || null,
        date: new Date().toISOString(),
      });
      if (insErr) throw insErr;
      onChange({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
      });
      setForm({ name: '', phone: '', email: '', address: '' });
      setCreating(false);
      onClientCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de créer le client.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          <i className="ri-user-3-line text-primary-500"></i>
          Bloc 2 · Client
        </h3>
        <button
          type="button"
          onClick={() => { setCreating((v) => !v); setError(null); }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer"
        >
          <i className={creating ? 'ri-close-line' : 'ri-user-add-line'}></i>
          {creating ? 'Annuler' : 'Nouveau client'}
        </button>
      </div>

      <Field label="Choisir dans la liste de mes clients">
        <select value={selectedId} onChange={(e) => pickClient(e.target.value)} className={`${inputClass} cursor-pointer`}>
          <option value="">— Saisie libre / nouveau client —</option>
          {clients.map((c) => (
            <option key={c.clientId} value={c.clientId}>
              {c.name || (c.clientId.startsWith('guest:') ? c.clientId.slice(6) : `Client #${c.clientId}`)}
              {c.phone ? ` · ${c.phone}` : ''}
            </option>
          ))}
        </select>
      </Field>

      {creating && (
        <div className="mb-3 p-3 rounded-lg bg-background-100">
          <p className="text-xs font-semibold text-foreground-700 mb-2">Création rapide d’un client</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
            <Field label="Nom *"><input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className={inputClass} placeholder="Nom du client" /></Field>
            <Field label="Téléphone"><input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className={inputClass} placeholder="06..." /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className={inputClass} placeholder="client@email.com" /></Field>
            <Field label="Adresse"><input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} className={inputClass} placeholder="Rue, ville" /></Field>
          </div>
          {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
          <button
            type="button"
            onClick={createClient}
            disabled={busy}
            className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Enregistrer le client'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
        <Field label="Nom / raison sociale *">
          <input value={customer.name} onChange={(e) => onChange({ name: e.target.value })} className={inputClass} placeholder="Nom du client" />
        </Field>
        <Field label="Téléphone">
          <input value={customer.phone} onChange={(e) => onChange({ phone: e.target.value })} className={inputClass} placeholder="06..." />
        </Field>
        <Field label="Email">
          <input type="email" value={customer.email} onChange={(e) => onChange({ email: e.target.value })} className={inputClass} placeholder="client@email.com" />
        </Field>
        <Field label="ICE du client (BtoB)">
          <input value={customer.ice} onChange={(e) => onChange({ ice: e.target.value })} className={inputClass} placeholder="15 chiffres" />
        </Field>
      </div>
      <Field label="Adresse complète">
        <input value={customer.address} onChange={(e) => onChange({ address: e.target.value })} className={inputClass} placeholder="Rue, quartier, ville" />
      </Field>
    </section>
  );
}